import React from 'react';

interface IForecastPeriodModifiedBannerArgs {
  prevValue: string;
  newValue: string;
  userName: string;
  date: string;
}
export const L10nForecasting = {
  settings: {
    pipelineLabel: 'Pipeline',
    pipelinePlaceholder: 'Select pipeline',
    automateForecastTitle: 'Automate Forecast Categories',
    automateForecastSubTitle:
      ' Automatically update the forecast category property every time a deal stage changes in your CRM.',
    forecastCategoryTitle: 'Forecast Category',
    forecastCategorySubTitle: 'These values are pulled in from your CRM.',
    forecastSettingsTitle: 'Forecast settings',
    configurationSettingsTitle: 'Configuration',
    dashboardSettingsTitle: 'Dashboard options',
    reminderSettingsTitle: 'Reminder settings',
    forecastStructureTitle: 'Forecast structure',
    forecastSubmissionSettingsTitle: 'Forecast submission settings',
    forecastDashboardSettingsTitle: 'Forecast dashboard settings',
    accessControlTitle: 'Forecast and Targets access permissions',
    totalAmountLabel: 'Total Amount',
    totalAmountSubtitle: 'The total deal amount as recorded in your CRM.',
    weightedAmountLabel: 'Weighted Amount',
    weightedAmountSubtitle:
      'Calculated as Deal Amount × Deal Probability. The deal probability is an imported property from your CRM.',
    weightedAmountTooltip:
      'Calculated as Deal Amount × Deal Probability.\nThe deal probability is an imported property from your CRM.',
    forecastAmountTitle: 'Forecast Amount',
    forecastAmountSubTitle:
      'Choose how Forecast Amount is displayed in your forecasting view. Forecast Amount is setup in your CRM.',
    weightedAmountComingSoon: 'Coming soon',
    weightedUpdating: 'Updating...',
    forecastPeriodTitle: 'Forecast period',
    forecastPeriodSubTitle:
      'Select forecast period that will be set as primary for your organization.',
    forecastPeriodSelectSubtitle: 'Select forecast period',
    forecastSubmissionReminderTitle: 'Forecast submission reminder',
    forecastSubmissionReminderSubtitle1:
      'Set the frequency for submission reminders to update forecasts. Only team members in forecast teams will receive them.',
    forecastSubmissionReminderSubtitle2:
      'Only team members in forecast teams will receive reminders to update forecasts. They’ll be sent in member’s current timezone.',
    periodModalTitle: 'Change forecast period?',
    monthlyApplyall:
      'Apply this target amount for all months from the current month onwards.',
    quarterlyApplyall:
      'Apply this target amount for all quarters from the current quarter onwards.',
    targetAlreadyCreatedTooltip: 'Target already created for this quarter',
    periodSettingChangeText:
      'will remove all revenue target and forecast submission data for all of your pipelines from the past',
    enterAmount: 'Enter the amount',
    applyTarget: 'Apply target',
    settingsUpdatedSuccess: 'Forecast settings updated successfully',
    settingsUpdatedError: 'Failed to update forecast settings',
    forecastAmountFieldTitle: 'Forecast amount field',
    forecastAmountFieldSameLabel: 'Same amount field for all',
    forecastAmountFieldSameSubtitle:
      'Use the selected or default deal amount field for every value',
    forecastAmountFieldDifferentLabel: 'Different amount field for every split',
    forecastAmountFieldDifferentSubtitle:
      'Map each property value to a different amount field.',
    segmentAmountMappingSegmentHeader: 'Segment',
    segmentAmountMappingPropertyHeader: 'CRM property',
    segmentAmountMappingTooltip: 'Only numeric amount fields are supported.',
    segmentAmountMappingDropdownTitle: 'Choose field from deal object',
    segmentAmountMappingSelectPlaceholder: 'Select',
    segmentAmountMappingRequired: 'Required',
    segmentAmountMappingNoOptions: 'No compatible CRM properties available',
    segmentAmountMappingBanner: ({
      currencyCode,
      currencySymbol
    }: {
      currencyCode: string;
      currencySymbol: string;
    }) =>
      `Avoma does not convert currencies across mapped fields. Amounts will be displayed in ${currencyCode} (${currencySymbol}) based on your Avoma currency setting.`,
    reminderSettingsSaveSuccess:
      'Forecast reminder settings saved successfully',
    reminderSettingsSaveError: 'Failed to save forecast reminder settings',
    reminderSettingsDeleteSuccess: 'Forecast reminder disabled successfully',
    reminderSettingsDeleteError: 'Failed to disable forecast reminder',
    reminderChannelEmailTitle: 'Email',
    reminderChannelEmailSubtitle:
      'Email reminders will be sent to all team members who are part of the forecast teams',
    reminderChannelSlackTitle: 'Slack',
    reminderChannelSlackSubtitle: () => (
      <React.Fragment>
        Slack reminders will be sent as{' '}
        <strong className='text-gunmetal'>direct messages</strong> to all team
        members who are part of the forecast teams
      </React.Fragment>
    ),
    reminderChannelSlackNotConnectedAdminPrefix: 'Connect Slack in ',
    reminderChannelSlackNotConnectedAdminSuffix:
      ' to enable Slack reminders for forecast submissions.',
    reminderChannelSlackNotConnectedOnlyAdminsPrefix: () => (
      <React.Fragment>
        Only <span className='font-bold'>Avoma admins</span> can connect Slack
        in{' '}
      </React.Fragment>
    ),
    reminderChannelSlackNotConnectedOnlyAdminsSuffix:
      ' to enable Slack reminders for forecast submissions.'
  },
  dealStage: 'Deal Stage',
  viewDealStage: 'View Deal Stage',
  viewForecastCategory: 'View Forecast Category',
  viewAggregateForecastCategory: 'View Aggregate Forecast Category',
  forecastCategory: 'Forecast Category',
  aggregateForecastCategory: 'Aggregate Forecast Category',
  aggregatedForecast: 'Aggregated Forecast',
  closedWon: 'Closed Won',
  closedLost: 'Closed Lost',
  gap: 'Gap',
  noTargetGapTooltip: 'Set a target to calculate Gap',
  forecastSubmit: 'Forecast Submit',
  includedDealsValue: 'Included Deals Value',
  subtotal: 'Subtotal',
  target: 'Targets',
  targets: {
    createNew: 'Create a New Target',
    filters: {
      member: 'Member',
      memberLabel: 'Member',
      teamAndMember: 'Team & Member',
      team: 'Team (1)',
      teamLabel: 'Team',
      filterByPipeline: 'Filter by Pipeline',
      filterByOpportunityType: 'Filter by Opportunity Type',
      filterBySegment: 'Filter by Segment',
      filterByStatus: 'Filter by Status',
      filterByYear: 'Filter by Year',
      pipelineFilterTriggerText: 'Pipeline',
      opportunityTypeFilterTriggerText: 'Opportunity Type',
      segmentFilterTriggerText: 'Segment',
      statusFilterTriggerText: 'Status',
      yearFilterTriggerText: 'Year'
    },
    createDisabledTooltip: 'Only admins and managers can create targets',
    deleteTargets: 'Delete Targets?',
    emptyTable: 'You have not set any targets yet'
  },
  pipeline: 'Pipeline',
  pipelineTooltip:
    'Pipeline filters the deals based on the pipeline it belongs to.',
  forecastFor: 'Forecast for:',
  filtersLabel: 'Filters',
  viewSplit: (count: number) => `View split (${count})`,
  deals: 'Deals',
  dealFilters: 'Deal filters',
  allDealFilters: 'All deal filters',
  allFilters: 'All filters',
  companies: 'Companies',
  forecasting: 'Forecast',
  reports: 'Reports',
  noResultsOpportunities: 'No deals found for the applied filters.',
  viewTargets: 'View Targets',
  createNewTarget: 'Create a New Target',
  valuesAreBasedForecastSettings:
    'Values are based on the display amount setting that you can modify at forecast settings',
  pipelineChartInfo:
    'Total of all open deals for the selected pipeline, teams, members and close date period',
  revenueIntelligence: 'Revenue Intelligence',
  crmRemoveAllTeamsMsg:
    'If you remove selected teams, all teams from your organization will be shown in forecasting page.',
  historyAmountTooltip: 'Forecast Submitted Amount (Deals Submitted Amount)',
  teamChartInfo:
    'Target values are based on the targets set for the selected teams or members',
  reportTabs: {
    dealsRisk: 'Deals Risk',
    pipelineTrend: 'Pipeline Trend',
    pipelinePerformance: 'Pipeline Performance'
  },
  forecastSubmissionNotes: 'Add a note on what’s changed',
  accessControl: {
    notEligibleToCreateForecastSubmission:
      'You can’t submit a forecast.\nYou may not have permission, or you may not be on a team enabled for forecasting.',
    forecastSubmissionDisabledTooltip:
      'You do not have permission to edit this forecast submission',
    atleastOneDealTooltip: 'Please select at least one deal',
    pipeline: 'Pipeline',
    period: 'Period',

    yourSubmission: 'Final submission',
    forecastSubmssionTextAreaPlaceholder:
      'e.g., Moved XFactor to Commit after verbal confirmation from buyer. Reduced pipeline estimate due to delayed procurement at NovaTech.',
    forecastSubmissionSubtitle:
      'Select Deals to submit for above Forecast Period',
    dealStatus: 'Deal Status',
    dealStage: 'Deal Stage',
    deals: 'Deals',
    updateForecast: 'Update Forecast',
    targetCreateSuccess: 'Targets created successfully',
    targetUpdateSuccess: 'Targets updated successfully',
    timePeriod: 'Time Period',
    timePeriodDisabledTooltip: 'Target cannot be set for the past duration',
    applyTargets: 'Apply Targets',
    createTarget: 'Create Targets',
    manageTargets: 'Manage Targets',
    submitForecast: 'Submit Forecast',
    forecastSettings: 'Forecast Settings',
    openForecastSettings: 'Open Forecast Settings',
    goToManageTargets: 'Go to Manage Targets',
    onlyAdminsCanUpdate: 'Only Admins can update this setting',
    noTeamsAvailable: 'No teams available',
    selectTeam: 'Select Team',
    searchTeams: 'Search Teams',
    allTeamsTooltip:
      'All selected teams in the above forecast teams can access or modify forecasts and targets of all selected teams.',
    userTeamsTooltip:
      'The team members can access or modify the data of all members part of their primary and nested teams.',
    onlyManagerTooltip:
      'Only team managers(Set in avoma teams setting) can access or modify.',
    userOwnTooltip: 'Team members can access or modify the data of their own.',
    specificTeamsTitle: 'Specific additional teams',
    specificTeamsSubtitle:
      'e.g. Allow your finance team to view or modify forecast',
    checkboxDisabledTooltips: {
      allUsers:
        'Since all users permissions is enabled, every other permission is also enabled by default',
      usersTeamsEnabled:
        "Since members can access their team's data, they can also access their own data",
      userTeamsEnabledForManager:
        "Since members can access their team's data, managers can automatically access the data",
      oneSelectionNeeded: 'At least one selection is mandatory.'
    },
    settingNotAvailableTooltip:
      'This option is disabled since “View Forecast & Targets” is not enabled for the same',
    forecastTeamSelectTitle: 'For teams selected in “Forecast teams”',
    helpPopover: {
      title: 'How Access permission works?',
      accesspermission: 'Access permission',
      bodyText1:
        'This will allow access of the forecast board and targets to all members of the organization or teams they are part of or only admins and team managers or selected teams.',
      bodyText2:
        'For the nested teams, the targets will be visible to the respective parent teams.',
      bodyText3:
        'Users not part of any teams will not be able to see any targets or the forecasting board or targets.',
      bodyText4:
        'Users part of the selected teams need to be on a business plan to access or modify the forecasts or targets.'
    },
    adminBanner: "Admins can access all selected team's data at all times.",
    forecastTeamUpdateError: 'Error occurred while updating forecast team',
    removeAllTeamSuccess: 'All teams removed successfully',
    forecastTeams: 'Forecast Teams',
    forecastTeamsSubtitle:
      'Select teams you want to show on forecasting and target page. It will show all teams if none selected.',
    clearAll: 'Clear all',
    removeForecastTeamLabel: (teamName: string) => `Remove ${teamName}`,
    specificTeamsTooltip:
      'Selected teams can access or modify data for the defined permission set.',
    forecastUserPopoverLabel: 'Forecast users',
    teamNoAccessBreadcrumb:
      "You don't have access to this team's forecast data",
    noAccessTitle: "You don't have access to Forecast",
    noAccessSubtitle:
      "You may not be part of a forecast team, or view access hasn't been enabled for you. Contact your admin to get access.",
    viewForecastSettings: 'View forecast settings',
    learnMore: 'Learn more'
  },
  submissionOption: {
    title: 'Forecast dashboard option',
    descp:
      'Members and Managers can set forecast based on any on the selection.',
    mostLikely: 'Most Likely',
    somewhatLikely: 'Somewhat Likely',
    leastLikely: 'Least Likely',
    allDealsAreBestCase: 'All the deals selected are considered as best case',
    dealsDistributedInThreeCategories:
      'Deals can be distributed in 3 categories',
    normal: 'Normal',
    advanced: 'Advanced',
    changeOption: 'Change “Forecast submission option”?',
    existing: 'Existing',
    new: 'New',
    withThisChange: 'With this change,',
    switchToAdvancedPoint1:
      '“Most Likely” data of past months/quarters is preserved as per selected forecast period but somewhat and least case will be empty.',
    switchToAdvancedPoint2:
      'For current and future months/quarters as per selected forecast period you’ll be able to submit forecast as per advance options.',
    switchConfirmation:
      'Are you sure you want to change the submission option?',
    changesPlaceholder:
      'What’s changed? Add a note to let your team or manager know about the changes.',
    forecastDistribution: 'Adjust forecast amount & add notes',
    forecastDistributionSubtitle:
      'The amounts below are auto-calculated from all deals in each category. Override any category if you believe the actual close amount will differ.',
    contactUsToChange:
      'Please contact us to change settings from “Advanced” to “Normal”.',
    dealStages: 'Simple Forecast by Deal Stage',
    unknownPipeline: 'Other',
    simpleForecast: 'Simple Forecast by Forecast Category',
    aggregatedForecast: 'Aggregated Forecast',
    selectForecastCategories: 'Select Forecast Categories',
    selectCategory: 'Select Category',
    periodTooltip:
      'Period filters the deals based on the deal close dates belonging to the selected period.',
    yourSubmission: 'Final submission',
    includedDealsValue: 'Included Deals Value',
    selectedDealsAmount: 'Selected Deals Amount',
    selectedDealsWeightedAmount: 'Selected Deals Weighted Amount',
    segmentSelectorLabel: 'Segment',
    selectSegmentPlaceholder: 'Select segment',
    noForecastCategorySelected:
      'The forecast category for this deal is either empty or does not map to any aggregated forecast category. Please change the forecast category for this deal',
    noOppurtunitiesSelected: (aggregateCategory: string) =>
      `No deals have been selected that are mapped to the "${aggregateCategory}" aggregated forecast category`,
    targetOverview: 'Target Overview',
    targetAndPipelineOverview: 'Target and Pipeline Overview',
    targetAndPipelineOverviewTooltip:
      'Based on selected pipeline, close date, and team or member filters',
    forecastSubmitted: 'Forecast Submitted',
    viewSplit: (count: number) => `View split (${count})`,
    collapseSplit: (count: number) => `Collapse (${count})`,
    target: 'Target',
    forecastSettings: 'Forecast Settings',
    noTeamsAdded:
      'No teams have been set up to display the forecast. Please create teams and add them in',
    selectDeals: 'Select deals to submit for above forecast period',
    associatedDeals: 'Review and select deals',
    associatedDealsSubtitle:
      'Deals closing this period are included by default. Confirm which should be included in your forecast.',
    filterDealsForSubmission: 'Filter deals for submission',
    filterDealsForSubmissionSubtitle:
      'This submission will include only deals that match the below filters',
    reviewAndUpdateForecastCategories: 'Review & update forecast categories',
    reviewAndUpdateForecastCategoriesSubtitle:
      'All deals in each category are included in your forecast. Move deals to a different category or change close dates as needed.',
    reviewNextCategory: (category: string) => `Review: ${category}`,
    continueToSubmission: 'Continue to submission',
    searchForADeal: 'Search for a deal',
    searchForDeals: 'Search for deals',
    dealsInCategoryWillBeSubmitted: (dealCount: number, category: string) =>
      `${dealCount} ${
        dealCount === 1 ? 'deal' : 'deals'
      } in ${category} will be submitted`,
    reset: 'Reset',
    notesAboutChanges: 'Notes about what’s changed',
    totalDealsAmount: 'Total Deals Amount',
    useAmount: 'Use',
    enterAmountPlaceholder: 'Enter amount',
    totalWeightedDealsAmount: 'Total Deals Weighted Amount',
    forecastDealsFor: 'Forecast deals for',
    forecastSubmissionFor: 'Forecast Submission for',
    submissionHistoryFor: 'Submission History for',
    viewHistory: 'View Submission History',
    viewSubmissionHistory: 'View Submission History',
    submission: 'Submission',
    finalSubmission: 'Final submission',
    submissionSummary: 'Submission summary',
    rollupSummary: 'Rollup summary',
    updatedBy: 'Updated by',
    you: 'You',
    yourFinalSubmission: 'Your final submission',
    enterFinalForecastAmountAndAddNote:
      'Enter your final forecast amount and add a note',
    enterFinalForecastAmount: 'Enter your final forecast amount',
    forecastAmountMaxTwoDecimals:
      'Forecast amount can have at most 2 decimal places',
    addANote: 'Add a note',
    noSubmissionsYet: 'No submissions yet.',
    openDealInANewTab: 'Open deal in new tab',
    columnChooser: {
      title: 'Choose which columns you want to see',
      visibility: 'Column visibility'
    },
    forecastDealDrawerDealsIn: (count: number) => `${count} deals in`,
    noDealsMatchingFilters: 'No deals found for the applied filters.',
    teamSubmissionRollup: "Roll up of teams' manual submission",
    noteNotAdded: 'Note not added.',
    summaryLabels: {
      user: {
        submission: {
          label: 'Forecast Submitted',
          tooltip: () => 'Forecast submitted by selected members.'
        },
        dealsValue: {
          label: 'Included deals value',
          tooltip: () => 'Value of deals included in the forecast by members.'
        }
      },
      team: {
        submission: {
          label: 'Forecast submitted by manager',
          tooltip: (teamName: string) =>
            `Forecast submitted by manager of ${teamName}.`
        },
        dealsValue: {
          label: 'Included deals value',
          tooltip: (teamName: string) =>
            `Value of deals included in the forecast by manager of ${teamName}.`
        }
      },
      all: {
        submission: {
          label: 'Overall forecast submitted',
          tooltip: () => 'Forecast submitted by team managers.'
        },
        dealsValue: {
          label: 'Included deals value',
          tooltip: () =>
            'Value of deals included in the forecast by team managers.'
        }
      }
    },
    selectedTeamsLabel: 'selected teams',
    dealSummaryLabels: {
      pipelineValue: 'Pipeline Value',
      coverage: 'Coverage',
      deals: 'Deals',
      averageDealValue: 'Avg. Deal Value'
    },
    expandEntireSubmission: 'Expand to see entire submission',
    collapseSubmission: 'Collapse submission',
    editForecastSubmission: 'Edit forecast submission',
    viewForecastSubmission: 'View forecast submission',
    estimateHowMuchYouWillClose:
      'Estimate how much you will close in this period',
    teamRollup: 'Team Rollup'
  },
  aggregatedCategoriesTooltip: (categories: string) =>
    `Aggregation of "${categories}"`,
  forecastPeriodModifiedBanner: ({
    prevValue,
    newValue,
    userName,
    date
  }: IForecastPeriodModifiedBannerArgs) => (
    <React.Fragment>
      Forecast period was modified from <strong>{prevValue}</strong> to{' '}
      <strong>{newValue}</strong> by <strong>{userName}</strong> on {date}. You
      can set new forecast submission from current quarter.
    </React.Fragment>
  ),
  openPipeline: 'Open Pipeline'
} as const;
